import React, { useEffect, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, Download, LogOut, FileText, User, Bell, X, Filter } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useB2BAuth, b2bApi } from "@/lib/b2bAuth";
import { API } from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function B2BDashboardPage() {
  const { user, logout } = useB2BAuth();
  const nav = useNavigate();
  const [docs, setDocs] = useState([]);
  const [products, setProducts] = useState([]);
  const [communications, setCommunications] = useState([]);
  const [filters, setFilters] = useState({ q: "", product_slug: "", language: "", sort: "recent" });
  const [showProfile, setShowProfile] = useState(false);

  useEffect(() => {
    if (user === null) { nav("/area-b2b", { replace: true }); return; }
    if (!user) return;
    load();
    b2bApi().get("/b2b/products").then((r) => setProducts(r.data || []));
    b2bApi().get("/b2b/communications").then((r) => setCommunications(r.data || []));
  }, [user]);

  const load = () => {
    const params = {};
    if (filters.q) params.q = filters.q;
    if (filters.product_slug) params.product_slug = filters.product_slug;
    if (filters.language) params.language = filters.language;
    if (filters.sort) params.sort = filters.sort;
    b2bApi().get("/b2b/documents", { params }).then((r) => setDocs(r.data || []));
  };

  useEffect(() => { if (user) load(); }, [filters]);

  if (user === undefined) return <div className="min-h-screen flex items-center justify-center text-muted-ink text-sm">Caricamento…</div>;
  if (user === null) return <Navigate to="/area-b2b" replace />;

  const download = async (doc) => {
    try {
      const { data } = await b2bApi().get(`/b2b/documents/${doc.id}/download-token`);
      // fetch as blob for real download experience
      const token = localStorage.getItem("bnx_b2b_token");
      const resp = await fetch(`${API}/b2b/documents/${doc.id}/file?t=${encodeURIComponent(data.token)}`);
      if (!resp.ok) throw new Error("Download non riuscito");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = data.filename || doc.filename || `${doc.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { toast.error("Download non riuscito"); }
  };

  const view = async (doc) => {
    try {
      const { data } = await b2bApi().get(`/b2b/documents/${doc.id}/download-token`);
      window.open(`${API}/b2b/documents/${doc.id}/file?t=${encodeURIComponent(data.token)}`, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error("Anteprima non disponibile"); }
  };

  const doLogout = async () => {
    await logout();
    toast.success("Sessione chiusa");
    nav("/area-b2b");
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="b2b-dashboard">
      <meta name="robots" content="noindex,nofollow" />
      <header className="border-b border-soft bg-white sticky top-0 z-30">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo />
            <span className="hidden md:inline text-xs uppercase tracking-editorial text-muted-ink">Area B2B</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowProfile(true)}
              data-testid="b2b-profile-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-soft px-4 py-1.5 text-xs uppercase tracking-editorial hover:bg-cream-warm"
            >
              <User className="h-3.5 w-3.5" /> Profilo
            </button>
            <button
              onClick={doLogout}
              data-testid="b2b-logout"
              className="inline-flex items-center gap-1.5 rounded-full bg-brand text-white px-4 py-1.5 text-xs uppercase tracking-editorial hover:bg-[#0f2020]"
            >
              <LogOut className="h-3.5 w-3.5" /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-8">
        {/* Welcome */}
        <div className="mb-8">
          <div className="text-xs uppercase tracking-editorial text-muted-ink mb-2">Bentornato</div>
          <h1 className="font-display text-4xl md:text-5xl text-brand leading-tight">
            Ciao {user.first_name}, {user.company}
          </h1>
          <p className="mt-2 text-muted-ink text-sm">Consulta e scarica i documenti disponibili per il tuo profilo.</p>
        </div>

        {/* Communications */}
        {communications.length > 0 && (
          <div className="mb-8 bg-white border border-soft p-5">
            <div className="text-xs uppercase tracking-editorial text-muted-ink mb-3 flex items-center gap-1.5">
              <Bell className="h-3.5 w-3.5" /> Comunicazioni
            </div>
            <div className="space-y-3">
              {communications.map((c) => (
                <div key={c.id} className="border-l-2 border-brand pl-4">
                  <div className="text-brand font-medium">{c.title}</div>
                  <div className="text-sm text-muted-ink mt-1">{c.body}</div>
                  {c.link && (
                    <a href={c.link} target="_blank" rel="noopener noreferrer" className="text-xs text-brand link-underline mt-1 inline-block">
                      Apri link
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Sidebar filters */}
          <aside className="md:col-span-3 space-y-6">
            <div>
              <label className="block text-xs uppercase tracking-editorial text-muted-ink mb-2">Ricerca</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-ink" />
                <Input
                  type="search"
                  placeholder="Cerca titolo o parola chiave"
                  value={filters.q}
                  onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                  className="pl-9 rounded-none border-soft bg-white"
                  data-testid="b2b-doc-search"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-editorial text-muted-ink mb-2">Prodotto</label>
              <ul className="space-y-1 max-h-96 overflow-auto">
                <li>
                  <button
                    onClick={() => setFilters((f) => ({ ...f, product_slug: "" }))}
                    className={`w-full text-left px-3 py-2 text-sm border ${
                      !filters.product_slug ? "bg-brand text-white border-brand" : "border-transparent hover:bg-cream-warm text-brand"
                    }`}
                    data-testid="b2b-product-filter-all"
                  >
                    Tutti i prodotti
                  </button>
                </li>
                {products.map((p) => (
                  <li key={p.slug}>
                    <button
                      onClick={() => setFilters((f) => ({ ...f, product_slug: p.slug }))}
                      className={`w-full text-left px-3 py-2 text-sm border flex items-center justify-between gap-2 ${
                        filters.product_slug === p.slug ? "bg-brand text-white border-brand" : "border-transparent hover:bg-cream-warm text-brand"
                      }`}
                      data-testid={`b2b-product-filter-${p.slug}`}
                    >
                      <span className="flex items-center gap-2">
                        <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${p.slug === "bionutrix-company" ? "bg-human" : p.line === "pet" ? "bg-pet" : "bg-human"}`}></span>
                        <span>{p.name}</span>
                      </span>
                      <span className="text-[10px] opacity-70">{p.doc_count}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-editorial text-muted-ink mb-2">Lingua</label>
              <select
                value={filters.language}
                onChange={(e) => setFilters((f) => ({ ...f, language: e.target.value }))}
                className="w-full h-9 border border-soft bg-white px-3 text-sm rounded-none"
              >
                <option value="">Tutte</option>
                <option value="it">Italiano</option>
                <option value="en">English</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-editorial text-muted-ink mb-2">Ordina</label>
              <select
                value={filters.sort}
                onChange={(e) => setFilters((f) => ({ ...f, sort: e.target.value }))}
                className="w-full h-9 border border-soft bg-white px-3 text-sm rounded-none"
              >
                <option value="recent">Più recenti</option>
                <option value="az">A-Z</option>
                <option value="date">Data documento</option>
              </select>
            </div>
          </aside>

          {/* Documents grid */}
          <div className="md:col-span-9">
            {docs.length === 0 ? (
              <div className="border border-dashed border-soft p-16 text-center bg-white">
                <FileText className="mx-auto h-8 w-8 text-muted-ink mb-3" />
                <p className="text-muted-ink text-sm">Nessun documento disponibile con questi filtri.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="b2b-doc-list">
                {docs.map((d) => {
                  const prod = products.find((p) => p.slug === d.product_slug);
                  const prodName = d.product_slug === "bionutrix-company" ? "BioNutrix (azienda)" : (prod?.name || "—");
                  return (
                    <article key={d.id} className="bg-white border border-soft p-5 flex flex-col">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="text-[10px] uppercase tracking-editorial text-muted-ink">
                          {prodName} {d.language && `· ${d.language.toUpperCase()}`} {d.version && `· v${d.version}`}
                        </div>
                        <div className="flex gap-1">
                          {d.is_new && <span className="text-[9px] uppercase px-1.5 py-0.5 bg-pet text-white">Nuovo</span>}
                          {d.is_updated && <span className="text-[9px] uppercase px-1.5 py-0.5 bg-human text-white">Aggiornato</span>}
                        </div>
                      </div>
                      <h3 className="font-display text-xl text-brand leading-tight mb-1">{d.title}</h3>
                      {d.description && <p className="text-sm text-muted-ink line-clamp-2">{d.description}</p>}
                      <div className="mt-3 text-[11px] text-muted-ink space-y-0.5">
                        {d.document_date && <div>Data: {d.document_date}</div>}
                        <div>Dimensione: {(d.size / 1024 / 1024).toFixed(2)} MB</div>
                      </div>
                      <div className="mt-4 flex gap-2">
                        <button
                          onClick={() => view(d)}
                          data-testid={`b2b-doc-view-${d.id}`}
                          className="flex-1 text-xs uppercase tracking-editorial px-3 py-2 border border-soft hover:bg-cream-warm"
                        >
                          Visualizza
                        </button>
                        <button
                          onClick={() => download(d)}
                          data-testid={`b2b-doc-download-${d.id}`}
                          className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs uppercase tracking-editorial px-3 py-2 bg-brand text-white hover:bg-[#0f2020]"
                        >
                          <Download className="h-3.5 w-3.5" /> PDF
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {showProfile && <ProfileDrawer onClose={() => setShowProfile(false)} />}
    </div>
  );
}

function ProfileDrawer({ onClose }) {
  const { user, refresh } = useB2BAuth();
  const [phone, setPhone] = useState(user?.phone || "");
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await b2bApi().put("/b2b-auth/profile", { phone });
      toast.success("Profilo aggiornato");
      refresh();
    } catch { toast.error("Errore"); } finally { setSavingProfile(false); }
  };

  const savePw = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error("Almeno 8 caratteri"); return; }
    setSavingPw(true);
    try {
      await b2bApi().post("/b2b-auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Password aggiornata");
      setOldPw(""); setNewPw("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); } finally { setSavingPw(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-md bg-white h-full overflow-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b border-soft flex justify-between items-center">
          <h2 className="font-display text-2xl text-brand">Profilo</h2>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>
        <div className="p-6 space-y-8">
          <div>
            <div className="text-xs uppercase tracking-editorial text-muted-ink mb-3">Dati account</div>
            <div className="text-sm space-y-1 text-brand">
              <div><b>{user.first_name} {user.last_name}</b></div>
              <div className="text-muted-ink">{user.company}</div>
              <div className="text-muted-ink">{user.email}</div>
              <div className="text-muted-ink text-xs">Tipologia: {user.account_type}</div>
            </div>
            <p className="text-xs text-muted-ink mt-3 italic">Per modificare azienda, email, P.IVA o tipologia, contatta l'amministratore.</p>
          </div>
          <form onSubmit={saveProfile} className="space-y-3">
            <div className="text-xs uppercase tracking-editorial text-muted-ink">Aggiorna telefono</div>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefono" className="rounded-none border-soft" />
            <button type="submit" disabled={savingProfile} className="rounded-full bg-brand text-white px-6 py-2 text-xs uppercase tracking-editorial disabled:opacity-50">{savingProfile ? "…" : "Salva"}</button>
          </form>
          <form onSubmit={savePw} className="space-y-3 border-t border-soft pt-6">
            <div className="text-xs uppercase tracking-editorial text-muted-ink">Cambia password</div>
            <Input type="password" required value={oldPw} onChange={(e) => setOldPw(e.target.value)} placeholder="Password attuale" className="rounded-none border-soft" autoComplete="current-password" />
            <Input type="password" required value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="Nuova password (min. 8)" className="rounded-none border-soft" autoComplete="new-password" />
            <button type="submit" disabled={savingPw} className="rounded-full bg-brand text-white px-6 py-2 text-xs uppercase tracking-editorial disabled:opacity-50">{savingPw ? "…" : "Aggiorna password"}</button>
          </form>
        </div>
      </div>
    </div>
  );
}
